import { Box, Button, List, ListItem, Stack, Typography } from "@mui/material";
import { DefaultPopUp } from "components/DefaultPopUp/DefaultPopUp";
import { FcbPopOverComponent } from "components/FcbPopOverComponent/FcbPopOverComponent";
import { HazardPopOverComponent } from "components/HazardPopOverComponent/HazardPopOverComponent";
import { DynamicAttributePopover } from "components/CustomCoursePopOver/DynamicAttributePopover";
import { attributeBindings } from "components/CustomCoursePopOver/attributeBindings";
import {
  useCourseGPSNameList,
  useCourseMapData,
  useEditableCourseItem,
  useSelectedCourseZoneType,
} from "hooks/CourseMapDetails";
import asset from "json/assets";
import { attributeFieldDefs, resolveElementKey } from "json/attributeFieldDefs";
import events from "json/events";
import ModalWrapper from "layout/ModalWrapper/ModalWrapper";
import NoificationModal from "pages/ModalPage/NoificationModal";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Draggable from "react-draggable";
import { useDispatch } from "react-redux";
import {
  setCourseShapePropertiesType,
  setCourseShapePropertiesSize,
  setCourseShapePropertiesDesc,
  clearCourseShapePropertiesType,
  clearCourseShapePropertiesSize,
  clearCourseShapePropertiesDesc,
  updateCourseMapData,
} from "redux/CourseMapSlice";
import eventEmitter from "services/event.emitter";
import {
  cascadeClear,
  getEffectiveDisplayLabel,
  getEffectiveOptions,
  normalizeLoadedValue,
} from "services/attributeFields.utils";
import { getModalProperties } from "services/utils";
import styles from "styles/component/CustomCoursePopOver.module.scss";
import * as _ from "underscore";

const EMPTY_ATTRS = { description: undefined, type: undefined, size: undefined };

// Titles that require manual user input before save. Anything resolveElementKey
// recognizes (it handles prefix collisions: path/centralpath, clubhouse/
// clubhousecenter, water/waterfall, tree/street) plus FCB/Hazard.
const needsUserInputTest = (title) => {
  if (!title) return false;
  const t = title.toLowerCase();
  if (t.includes("fcb") || t.includes("hazard")) return true;
  return resolveElementKey(title) !== null;
};

function CustomCoursePopOver(props) {
  const { title = "" } = props;
  const dispatch = useDispatch();
  const [errorMsg, setErrorMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [openConfModal, setOpenConfModal] = useState(false);
  const didSaveOnThisOpen = React.useRef(false);

  // Generic, data-driven attribute state.
  const [attrValues, setAttrValues] = useState(EMPTY_ATTRS);

  // FCB / Hazard keep their own state — their option sources are dynamic, not
  // constants.js-driven, so they don't fit the engine.
  const [markerType, setMarkerType] = useState();

  const CourseGPSNameListResponse = useCourseGPSNameList();
  const editableArea = useEditableCourseItem();
  const courseMapData = useCourseMapData();
  const zoneType = useSelectedCourseZoneType();
  const modalProperties = getModalProperties();

  const elementKey = useMemo(() => resolveElementKey(title), [title]);
  const fieldDef = elementKey ? attributeFieldDefs[elementKey] : null;
  const binding = elementKey ? attributeBindings[elementKey] : null;

  // ──────────────────────────────────────────────────────────────────────────
  // Keyboard delete
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isInputFocused =
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.isContentEditable;
      if (!isInputFocused && (event.key === "Delete" || event.key === "Backspace")) {
        if (!_.isEmpty(editableArea) && editableArea.label) {
          setOpenConfModal(true);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editableArea]);

  const confirmationAction = () => {
    eventEmitter.emit(events.resetDrawingMode);
    const element = editableArea.label;
    if (editableArea) {
      let temp_courseMapData = structuredClone(courseMapData);

      if (editableArea?.hole === -1) {
        const modifiedElement = element === "Nativearea" ? "NativeArea" : element;
        let pos = temp_courseMapData[modifiedElement]?.Shapes?.Shape.findIndex(
          (s) => s.id === editableArea._id,
        );

        if (pos > -1) {
          temp_courseMapData[modifiedElement].Shapes.Shape.splice(pos, 1);
          temp_courseMapData[modifiedElement].ShapeCount -= 1;
        }

        eventEmitter.emit(events.deleteCourseMapElement.courseMapVectorDelete, {
          idList: [editableArea._id],
          type: "facility",
        });
      } else {
        let temp_Holes = temp_courseMapData?.Holes.Hole;
        const selectedHole = editableArea?.hole - 1;

        if (temp_Holes?.[selectedHole]?.[element]) {
          let pos = temp_Holes[selectedHole][element]?.Shapes?.Shape.findIndex(
            (s) => s.id === editableArea._id,
          );

          if (pos > -1) {
            temp_Holes[selectedHole][element].Shapes.Shape.splice(pos, 1);
            temp_Holes[selectedHole][element].ShapeCount -= 1;
          }

          eventEmitter.emit(events.deleteCourseMapElement.courseMapVectorDelete, {
            idList: [editableArea._id],
            type: "hole",
          });

          temp_courseMapData.Holes.Hole = temp_Holes;
        }
      }

      dispatch(updateCourseMapData(temp_courseMapData));
    }
    setOpenConfModal(false);
    eventEmitter.emit(events.addMarkerToggle, { visible: false });
  };

  const handleCloseConfModal = () => setOpenConfModal(false);

  // ──────────────────────────────────────────────────────────────────────────
  // Open/close wiring
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onToggle = (e) => setOpen(Boolean(e.visible));
    const onTree = (e) => setOpen(Boolean(e.open));
    const onBunker = (e) => setOpen(Boolean(e.open));

    eventEmitter.on(events.addMarkerToggle, onToggle);
    eventEmitter.on(events.openTreeElementInModal, onTree);
    eventEmitter.on(events.openBunkerElementInModal, onBunker);

    return () => {
      eventEmitter.off(events.addMarkerToggle, onToggle);
      eventEmitter.off(events.openTreeElementInModal, onTree);
      eventEmitter.off(events.openBunkerElementInModal, onBunker);
    };
  }, []);

  // ──────────────────────────────────────────────────────────────────────────
  // Attribute change handler with cascade-clear
  // ──────────────────────────────────────────────────────────────────────────
  const handleAttrChange = useCallback(
    (slot, value) => {
      setAttrValues((prev) => {
        if (!fieldDef) return prev;
        const proposed = { ...prev, [slot]: value };
        return cascadeClear(fieldDef.fields, proposed, slot);
      });
    },
    [fieldDef],
  );

  const handleChangeMarkerType = (_event, value) => {
    if (!Number.isNaN(Number(value))) {
      setMarkerType(Number(value));
      return;
    }
    setMarkerType(value);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Save
  // ──────────────────────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    // FCB / Hazard validation
    const isFcbHazard =
      title?.toLowerCase()?.includes("fcb") || title?.toLowerCase()?.includes("hazard");
    if (isFcbHazard && !markerType) {
      setErrorMsg("Must select a type");
      return;
    }

    // Generic validation for the engine — only fields that are visible after
    // progressive reveal and dependency filtering can fail.
    if (fieldDef) {
      const missing = [];
      let priorAnswered = true;
      for (const f of fieldDef.fields) {
        const effective = getEffectiveOptions(f, fieldDef.fields, attrValues);
        if (effective.length === 0) continue;
        if (!priorAnswered) break;
        if (
          f.required &&
          (attrValues[f.slot] === undefined || attrValues[f.slot] === null)
        ) {
          missing.push(
            getEffectiveDisplayLabel(f, fieldDef.fields, attrValues).replace(
              /^SELECT\s+/i,
              "",
            ),
          );
        }
        if (attrValues[f.slot] === undefined || attrValues[f.slot] === null) {
          priorAnswered = false;
        }
      }
      if (missing.length) {
        setErrorMsg(`Must select ${missing.join(", ")}`);
        return;
      }
    }

    // Tree's add-shape draw event (preserves the legacy side-effect that
    // triggers Leaflet draw mode when all three Tree fields are filled).
    if (
      elementKey === "Tree" &&
      attrValues.description !== undefined &&
      attrValues.type !== undefined &&
      attrValues.size !== undefined &&
      editableArea?.label !== "Tree"
    ) {
      let z = zoneType?.type;
      if (zoneType?.type === "line") z = "polyline";
      eventEmitter.emit(events.draw, { event: "add", type: z });
    }

    // FCB / Hazard markerType dispatch
    if (isFcbHazard && markerType !== undefined) {
      dispatch(setCourseShapePropertiesType(markerType));
    }

    // Resolve each slot's save value. The spec says hidden/empty fields
    // persist as 0 (NONE — see Mapping UI/OVERVIEW.md: "size is sent = 0 for
    // None"). The same rule covers three otherwise-divergent cases uniformly:
    //   1. dependency-hidden (Water SAND bank hides Size)
    //   2. user-just-didn't-pick on a `required: false` field
    //   3. legacy stored 0 (NONE) round-tripping without UI change
    // Emitting an explicit value for every slot is also what prevents stale
    // Redux from a previous cascade-cleared child bleeding into the payload.
    const resolveSaveValue = (slot) => {
      const v = attrValues[slot];
      return v === undefined || v === null ? 0 : v;
    };

    if (fieldDef && binding) {
      for (const f of fieldDef.fields) {
        if (binding[f.slot]?.set) {
          dispatch(binding[f.slot].set(resolveSaveValue(f.slot)));
        }
      }
    }

    if (!_.isEmpty(editableArea)) {
      eventEmitter.emit(events.savePolygon);
    } else {
      const payload = {};
      if (isFcbHazard) payload.markerType = markerType;
      if (fieldDef && binding) {
        for (const f of fieldDef.fields) {
          const pk = binding[f.slot]?.payloadKey;
          if (pk) payload[pk] = resolveSaveValue(f.slot);
        }
      }
      eventEmitter.emit(events.addNewShape, payload);
    }

    setErrorMsg("");
    eventEmitter.emit(events.addMarkerToggle, { visible: false });
  }, [
    title,
    elementKey,
    fieldDef,
    binding,
    attrValues,
    markerType,
    editableArea,
    zoneType,
    dispatch,
  ]);

  const handleSaveRef = React.useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  // Auto-save during Add flow when the element doesn't need any input.
  useEffect(() => {
    const onToggle = (e) => {
      const isOpen = !!e?.visible;

      if (!_.isEmpty(editableArea)) {
        setOpen(true);
        didSaveOnThisOpen.current = false;
        return;
      }

      setOpen(isOpen);

      if (isOpen) {
        if (!didSaveOnThisOpen.current && !needsUserInputTest(title)) {
          didSaveOnThisOpen.current = true;
          Promise.resolve().then(() => handleSaveRef.current());
        }
      } else {
        didSaveOnThisOpen.current = false;
      }
    };

    eventEmitter.on(events.addMarkerToggle, onToggle);
    return () => eventEmitter.off(events.addMarkerToggle, onToggle);
  }, [title, editableArea]);

  // ──────────────────────────────────────────────────────────────────────────
  // Close — clear all element-specific Redux slots
  // ──────────────────────────────────────────────────────────────────────────
  // Sand/Bunker/PracticeBunker and Condo/Hotel/Business share clear reducers,
  // so we de-dupe by reference before dispatching.
  const allClearActions = useMemo(() => {
    const seen = new Set();
    seen.add(clearCourseShapePropertiesType);
    seen.add(clearCourseShapePropertiesSize);
    seen.add(clearCourseShapePropertiesDesc);
    for (const elt of Object.values(attributeBindings)) {
      for (const slot of Object.values(elt)) {
        if (slot?.clear) seen.add(slot.clear);
      }
    }
    return Array.from(seen);
  }, []);

  const dispatchAllClears = useCallback(() => {
    for (const clear of allClearActions) dispatch(clear());
  }, [dispatch, allClearActions]);

  const handleClose = () => {
    eventEmitter.emit(events.resetDrawingMode);
    dispatchAllClears();
  };

  // ──────────────────────────────────────────────────────────────────────────
  // editableArea init + cleanup
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!_.isEmpty(editableArea)) {
      const attrs = editableArea?.Attributes || {};

      if (editableArea?.hole === -1) {
        // Universal generic dispatch — every facility element ALSO sets the
        // generic shape* slots; the save pipeline reads them as a fallback.
        dispatch(setCourseShapePropertiesType(attrs.Type));
        dispatch(setCourseShapePropertiesSize(attrs.Size));
        dispatch(setCourseShapePropertiesDesc(attrs.Description));

        // Element-specific dispatch + local seed
        if (fieldDef && binding) {
          const slotMap = {
            description: attrs.Description,
            type: attrs.Type,
            size: attrs.Size,
          };
          const seeded = { ...EMPTY_ATTRS };
          for (const f of fieldDef.fields) {
            const raw = slotMap[f.slot];
            if (binding[f.slot]?.set && raw !== undefined && raw !== null) {
              dispatch(binding[f.slot].set(raw));
            }
            // "Open with no selection": legacy 0 -> undefined in the UI
            seeded[f.slot] = normalizeLoadedValue(raw);
          }
          setAttrValues(seeded);
        }
      } else {
        // Hole-context elements: Bunker / FCB / Hazard
        if (title?.toLowerCase()?.includes("bunker")) {
          if (binding) {
            if (binding.description?.set && attrs.Description !== undefined) {
              dispatch(binding.description.set(attrs.Description));
            }
            if (binding.type?.set && attrs.Type !== undefined) {
              dispatch(binding.type.set(attrs.Type));
            }
            setAttrValues({
              description: normalizeLoadedValue(attrs.Description),
              type: normalizeLoadedValue(attrs.Type),
              size: undefined,
            });
          }
        } else if (title?.toLowerCase()?.includes("fcb")) {
          const selectedMarkerType = editableArea?.place;
          setMarkerType(selectedMarkerType);
          dispatch(setCourseShapePropertiesType(selectedMarkerType));
        } else if (title?.toLowerCase()?.includes("hazard")) {
          const GPSNameObject = CourseGPSNameListResponse.find(
            (s) => s.id === editableArea?.gpsNameID,
          );
          if (GPSNameObject) {
            setMarkerType(GPSNameObject.id);
            dispatch(setCourseShapePropertiesType(GPSNameObject.id));
          }
        }
      }
    }

    return () => {
      setErrorMsg("");
      setAttrValues(EMPTY_ATTRS);
      setMarkerType();
      dispatchAllClears();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, open, editableArea?._id]);

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────
  const getErrorMsgComponent = () => (
    <Box className={styles.errorBox}>
      <Stack direction={"row"} alignItems={"center"}>
        <Box className={styles.left_icon}>
          <img src={asset.update} alt="#Info" />
        </Box>
        <Box className={styles.info}>
          <Typography variant="body1">{errorMsg}</Typography>
        </Box>
      </Stack>
    </Box>
  );

  const isAddingMode = (s) => s?.includes("Add");

  const getPopOverBody = () => {
    // FCB / Hazard — keep legacy components (API-driven options)
    if (isAddingMode(title) && title?.toLowerCase()?.includes("fcb")) {
      return (
        <FcbPopOverComponent
          markerType={markerType}
          handleChangeMarkerType={handleChangeMarkerType}
          errorMsg={errorMsg}
          handleSave={handleSave}
          errorComponent={getErrorMsgComponent}
        />
      );
    }
    if (title?.toLowerCase()?.includes("hazard")) {
      return (
        <HazardPopOverComponent
          markerType={markerType}
          handleChangeMarkerType={handleChangeMarkerType}
          errorMsg={errorMsg}
          handleSave={handleSave}
          errorComponent={getErrorMsgComponent}
        />
      );
    }

    // Engine path — every constants-driven element flows through here
    if (elementKey) {
      return (
        <DynamicAttributePopover
          elementKey={elementKey}
          values={attrValues}
          onChange={handleAttrChange}
          errorMsg={errorMsg}
          errorComponent={getErrorMsgComponent}
          handleSave={handleSave}
        />
      );
    }

    return <DefaultPopUp errorMsg={errorMsg} handleSave={handleSave} handleClose={handleClose} />;
  };

  return (
    <Box className={`${styles.sub_popover} ${styles.draggableDiv}`}>
      <Draggable bounds={"#DraggableContainer"} defaultPosition={{ x: 0, y: 0 }}>
        <Box
          className={`${styles.poover_content} ${styles.default_poover_content} custom_popover_content`}
          sx={{ display: open ? "block" : "none" }}
        >
          <Box className={styles.poover_head}>
            <Typography variant="h5">
              {title === "Add fcb" ? "Add F/C/B" : title}
            </Typography>
            <List>
              <ListItem>
                <Button onClick={handleClose}>
                  <img src={asset.close} alt="" />
                </Button>
              </ListItem>
            </List>
          </Box>

          {getPopOverBody()}
        </Box>
      </Draggable>
      <ModalWrapper
        open={openConfModal}
        onClose={handleCloseConfModal}
        handleClose={handleCloseConfModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <NoificationModal
          label="Delete Object Confirmation "
          backgroundColor={modalProperties.info.backgroundColor}
          image={modalProperties.warning.icon}
          title={`<span style='font-size:14px'>Are you sure you want to delete
               <br/> this map object?
               </span>`}
          rejCallBack={handleCloseConfModal}
          affrCallBack={confirmationAction}
          affrBtnTxt={"Continue"}
          rejBtnTxt={"Cancel"}
        />
      </ModalWrapper>
    </Box>
  );
}

export default CustomCoursePopOver;
